import { IElement } from "core-integration/src/store/elements/types";
import LockedIcon from "plos/src/img/icons/locked.svg?react";
import { getDifficultyColor } from "plos/src/styles/difficultyColors";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { useTranslation } from "react-i18next";
import { getPriceChangeStatus } from "../utils";
import styles from "./priceChangeStatus.module.css";

interface PriceChangeStatusProps {
  element: IElement;
}

const PriceChangeStatus = ({ element }: PriceChangeStatusProps) => {
  const { t } = useTranslation("priceChanges");

  const rating = getPriceChangeStatus(element);

  // If player price is locked, prioritise showing this over any other status
  if (element.price_change_locked_until) {
    const daysUntilUnlock = Math.ceil(
      (new Date(element.price_change_locked_until).getTime() - Date.now()) /
        (1000 * 60 * 60 * 24),
    );

    return (
      <span className={`${styles.chip} ${getDifficultyColor(3)}`}>
        <div>
          <LockedIcon />
        </div>
        {t("status.unlocksIn", "Unlocks in {{count}} days", {
          count: daysUntilUnlock,
        })}
      </span>
    );
  }

  // If player price is calibrating, prioritise showing this over change status
  if (element.price_change_calibrating) {
    return (
      <span className={`${styles.chip} ${getDifficultyColor(3)}`}>
        {t("status.calibrating", "Calibrating status")}
      </span>
    );
  }

  const label: Record<typeof rating, string | null> = {
    1: t("status.veryLikelyToRise", "Very likely to rise"),
    2: t("status.likelyToRise", "Likely to rise"),
    3: t("status.noChange", "Unlikely to change"),
    4: t("status.likelyToDrop", "Likely to drop"),
    5: t("status.veryLikelyToDrop", "Very likely to drop"),
  };

  return (
    <span className={`${styles.chip} ${getDifficultyColor(rating)}`}>
      <span className={visuallyHidden}>
        {t("status.label", "Price change status: ")}
      </span>
      {label[rating]}
    </span>
  );
};

export default PriceChangeStatus;
